/**
 * Pure builders for FFmpeg command lines. No FFmpeg instance, no DOM, so
 * these are straightforward to unit test.
 */

export const FONT_FILE = "DejaVuSans-Bold.ttf";
export const MUSIC_FILE = "music.mp3";

export const normalizeHexColor = (color) => {
  const hex = String(color || "").replace("#", "");
  return /^[0-9a-fA-F]{6}$/.test(hex) ? hex : "ffffff";
};

/**
 * Arguments for a single highlight clip.
 * Seek and duration are input options so slow motion (setpts) lengthens the
 * output instead of being cut off.
 */
export const buildClipArgs = ({
  inputPath,
  outputName,
  startTime,
  duration,
  slowMotion = false,
  zoom = null,
  textFile = null,
  color = "#ffffff",
  preset = "fast",
  hasAudio = true,
}) => {
  const filters = [];

  if (zoom && zoom.enabled) {
    const xNormalized = (zoom.x || 0) / 200 + 0.5;
    const yNormalized = (zoom.y || 0) / 200 + 0.5;
    filters.push(
      `crop=iw/2:ih/2:(iw-iw/2)*${xNormalized}:(ih-ih/2)*${yNormalized}`,
      "scale=iw*2:ih*2"
    );
  }

  if (textFile) {
    filters.push(
      `drawtext=textfile='${textFile}':fontfile=${FONT_FILE}:fontcolor=0x${normalizeHexColor(color)}:fontsize=60:x=(w-tw)/2:y=60`
    );
  }

  // Quick dip-to-black transition between clips. Placed before setpts so a
  // slow-motion clip still fades out exactly at its (stretched) end.
  filters.push("fade=t=in:st=0:d=0.2");
  filters.push(`fade=t=out:st=${Math.max(0, duration - 0.2)}:d=0.2`);

  if (slowMotion) {
    filters.push("setpts=2.0*PTS");
  }

  const crf = preset === "ultrafast" ? "28" : "23";

  const args = [
    "-ss", String(startTime),
    "-t", String(duration),
    "-i", inputPath,
    "-map", "0:v:0",
    "-map", "0:a:0?",
    "-vf", filters.join(","),
    "-c:v", "libx264",
    "-preset", preset,
    "-crf", crf,
    "-pix_fmt", "yuv420p",
  ];

  if (hasAudio) {
    // Re-encode audio uniformly so the stream-copy concat always succeeds.
    args.push("-c:a", "aac", "-b:a", "192k", "-ar", "48000", "-ac", "2");
    if (slowMotion) args.push("-af", "atempo=0.5");
  }

  args.push("-movflags", "+faststart", outputName);
  return args;
};

/**
 * Arguments that lay a music track under the combined video.
 * The music is mixed with the original audio (when present), delayed to
 * `videoStartTime`, padded with silence and capped at the video length so a
 * short track can never truncate the video.
 */
export const buildMusicArgs = ({
  inputName,
  outputName,
  music,
  totalDuration,
  videoHasAudio,
}) => {
  const {
    musicStartTime = 0,
    videoStartTime = 0,
    fadeIn = false,
    fadeOut = false,
    volume = 1,
  } = music;

  const chain = [];
  const level = Math.max(0, Math.min(1, Number(volume)));
  if (level !== 1) chain.push(`volume=${level}`);
  if (fadeIn) chain.push("afade=t=in:st=0:d=2");

  const musicPlayLength = totalDuration - videoStartTime;
  if (fadeOut && musicPlayLength > 2) {
    chain.push(`afade=t=out:st=${(musicPlayLength - 2).toFixed(3)}:d=2`);
  }
  if (videoStartTime > 0) {
    chain.push(`adelay=${Math.round(videoStartTime * 1000)}:all=1`);
  }
  chain.push("apad");

  const filter = videoHasAudio
    ? `[1:a]${chain.join(",")}[m];[0:a][m]amix=inputs=2:duration=first:normalize=0[a]`
    : `[1:a]${chain.join(",")}[a]`;

  return [
    "-i", inputName,
    "-ss", String(musicStartTime),
    "-i", MUSIC_FILE,
    "-filter_complex", filter,
    "-map", "0:v:0",
    "-map", "[a]",
    "-c:v", "copy",
    "-c:a", "aac",
    "-b:a", "192k",
    "-t", String(totalDuration),
    "-movflags", "+faststart",
    outputName,
  ];
};
